import { useState } from 'react'
import { Link } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { useSettings, useUpdateSettings } from '../hooks/useSettings'

export default function Settings() {
  const { data: settings, isLoading, error } = useSettings()
  const updateMutation = useUpdateSettings()
  const [draft, setDraft] = useState<{
    electricity_rate_per_kwh: string
    labor_hourly_rate: string
    material_efficiency_factor: string
    default_margin_pct: string
    default_moms_cut_pct: string
  } | null>(null)
  const [saveMessage, setSaveMessage] = useState('')

  const activeDraft = draft ?? (settings
    ? {
        electricity_rate_per_kwh: String(settings.electricity_rate_per_kwh),
        labor_hourly_rate: String(settings.labor_hourly_rate),
        material_efficiency_factor: String(settings.material_efficiency_factor),
        default_margin_pct: String(settings.default_margin_pct),
        default_moms_cut_pct: String(settings.default_moms_cut_pct),
      }
    : {
        electricity_rate_per_kwh: '0.14',
        labor_hourly_rate: '20.00',
        material_efficiency_factor: '1.10',
        default_margin_pct: '60',
        default_moms_cut_pct: '25',
      })

  const handleSave = async () => {
    setSaveMessage('')
    try {
      await updateMutation.mutateAsync({
        electricity_rate_per_kwh: Number(activeDraft.electricity_rate_per_kwh),
        labor_hourly_rate: Number(activeDraft.labor_hourly_rate),
        material_efficiency_factor: Number(activeDraft.material_efficiency_factor),
        default_margin_pct: Number(activeDraft.default_margin_pct),
        default_moms_cut_pct: Number(activeDraft.default_moms_cut_pct),
      })
      setSaveMessage('Saved')
      setTimeout(() => setSaveMessage(''), 2000)
    } catch (e) {
      setSaveMessage('Failed: ' + (e as Error).message)
    }
  }

  if (isLoading) {
    return <div style={{ padding: 'var(--space-6)' }}>Loading…</div>
  }

  if (error || !settings) {
    return (
      <div style={{ padding: 'var(--space-6)' }}>
        <h1>Settings</h1>
        <p style={{ color: 'var(--color-danger)', marginTop: 'var(--space-4)' }}>
          Couldn&apos;t load settings: {(error as Error)?.message ?? 'no settings row found'}
        </p>
        <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
          If this is your first time, sign out and sign back in to trigger the auto-create.
        </p>
      </div>
    )
  }

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 560 }}>
      <h1 style={{ marginBottom: 'var(--space-6)' }}>Settings</h1>

      <section style={{ marginBottom: 'var(--space-6)' }}>
        <h2 style={{ fontSize: 'var(--font-size-base)', marginBottom: 'var(--space-3)' }}>
          Manage
        </h2>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {[
            { to: '/settings/printers', icon: '🖨️', label: 'Printers', sub: 'Add, edit, set wattage and purchase date' },
            { to: '/settings/hardware', icon: '🔩', label: 'Hardware library', sub: 'Saved items (keyrings, magnets) for fast pick' },
            { to: '/settings/consignees', icon: '🤝', label: 'Consignees', sub: 'Mom, partners — manage who sells your prints' },
            { to: '/reports', icon: '📊', label: 'Reports', sub: 'Profit by month, top products, filament ROI' },
            { to: '/activity', icon: '📜', label: 'Activity log', sub: 'Audit trail of sales, prints, refunds, maintenance' },
            { to: '/settings/export', icon: '⬇️', label: 'Export CSV', sub: 'Download your data as spreadsheet files' },
          ].map((link) => (
            <li key={link.to} style={{ marginBottom: 'var(--space-2)' }}>
              <Link
                to={link.to}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-3)',
                  background: 'var(--color-surface)',
                  borderRadius: 'var(--radius-md)',
                  textDecoration: 'none',
                  color: 'var(--color-text)',
                }}
              >
                <span style={{ fontSize: '1.5rem' }}>{link.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{link.label}</div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                    {link.sub}
                  </div>
                </div>
                <span style={{ color: 'var(--color-text-faint)' }}>›</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section style={{ marginBottom: 'var(--space-6)' }}>
        <h2 style={{ fontSize: 'var(--font-size-base)', marginBottom: 'var(--space-3)' }}>
          Pricing inputs
        </h2>

        <Field
          label="Electricity rate ($/kWh)"
          value={activeDraft.electricity_rate_per_kwh}
          onChange={(v) => setDraft({ ...activeDraft, electricity_rate_per_kwh: v })}
          hint="US average is $0.14. Check your utility bill for exact rate."
        />

        <Field
          label="Labor rate ($/hr)"
          value={activeDraft.labor_hourly_rate}
          onChange={(v) => setDraft({ ...activeDraft, labor_hourly_rate: v })}
          hint="What you'd pay yourself for post-processing time. PFA default: $20."
        />

        <Field
          label="Material efficiency factor"
          value={activeDraft.material_efficiency_factor}
          onChange={(v) => setDraft({ ...activeDraft, material_efficiency_factor: v })}
          hint="Multiplier for filament waste (purges, retraction). PFA default: 1.10."
        />

        <Field
          label="Default margin %"
          value={activeDraft.default_margin_pct}
          onChange={(v) => setDraft({ ...activeDraft, default_margin_pct: v })}
          hint="Which suggested price (50/60/70%) to highlight as recommended."
        />

        <Field
          label="Default mom's cut %"
          value={activeDraft.default_moms_cut_pct}
          onChange={(v) => setDraft({ ...activeDraft, default_moms_cut_pct: v })}
          hint="Default commission for consignees. Per-product overrides on the product page."
        />
      </section>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
        <button
          onClick={handleSave}
          disabled={updateMutation.isPending}
          style={{
            padding: 'var(--space-3) var(--space-5)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
          }}
        >
          {updateMutation.isPending ? 'Saving…' : 'Save'}
        </button>
        {saveMessage && (
          <span style={{
            color: saveMessage === 'Saved' ? 'var(--color-success)' : 'var(--color-danger)',
            fontSize: 'var(--font-size-sm)',
          }}>
            {saveMessage}
          </span>
        )}
      </div>

      {import.meta.env.DEV && <SentrySmokeTest />}
    </div>
  )
}

// Stripped from production builds via the import.meta.env.DEV guard above.
// Lets us prove the error-monitoring pipeline works end-to-end.
function SentrySmokeTest() {
  const [status, setStatus] = useState<string>('')
  return (
    <section style={{ marginTop: 'var(--space-6)', paddingTop: 'var(--space-4)', borderTop: '1px dashed var(--color-border)' }}>
      <h2 style={{ fontSize: 'var(--font-size-base)', marginBottom: 'var(--space-2)' }}>Dev — Sentry smoke test</h2>
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-3)' }}>
        Sends one tagged event to Sentry so you can verify the dashboard sees it.
        Not present in production builds.
      </p>
      <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
        <button
          onClick={() => {
            const eventId = Sentry.captureException(
              new Error(`Sentry smoke test ${new Date().toISOString()}`),
            )
            setStatus(`Sent: ${eventId}`)
          }}
          style={{
            padding: 'var(--space-2) var(--space-4)',
            background: 'transparent',
            color: 'var(--color-danger)',
            border: '1px solid var(--color-danger)',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
          }}
        >
          Send test error
        </button>
        {status && (
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontFamily: 'monospace' }}>
            {status}
          </span>
        )}
      </div>
    </section>
  )
}

function Field({ label, value, onChange, hint }: {
  label: string
  value: string
  onChange: (v: string) => void
  hint?: string
}) {
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label style={{
        display: 'block',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
        marginBottom: 'var(--space-1)',
      }}>
        {label}
      </label>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: 'var(--space-3)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border)',
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
        }}
      />
      {hint && (
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginTop: 'var(--space-1)' }}>
          {hint}
        </p>
      )}
    </div>
  )
}
